/**
 * 🎯 DRIVER DISCOUNT CAMPAIGNS SERVICE
 * Manages driver-driven discount campaigns created by admin
 * Backend: https://polarride-api-17dr.vercel.app/api/v1/admin/driver-campaigns
 */

import { getApiBaseUrl } from '@/config/environment';

export type CampaignStatus = 'draft' | 'active' | 'scheduled' | 'ended' | 'cancelled';
export type DiscountType = 'percentage' | 'flat';

export interface DriverParticipation {
  driverId: string;
  driverName: string;
  driverCode: string;
  discountType: DiscountType;
  discountValue: number;
  optedInAt: string;
  bookingsGenerated?: number;
  revenueGenerated?: number;
}

export interface DriverCampaign {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  status: CampaignStatus;
  suggestedDiscountRange: {
    min: number;
    max: number;
  };
  allowedDiscountTypes?: DiscountType[];
  targetDrivers?: string[]; // Empty = all drivers
  participation?: DriverParticipation[];
  statistics?: {
    activeParticipants: number;
    totalParticipants: number;
    avgDiscount: number;
  };
  stats?: {
    totalDriversNotified: number;
    driversParticipating: number;
    averageDiscount: number;
    totalBookings: number;
    totalRevenue: number;
  };
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  pushedAt?: string;
}

export interface CampaignFilters {
  status?: CampaignStatus | 'all';
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface CampaignStats {
  totalCampaigns: number;
  activeCampaigns: number;
  totalParticipation: number;
  averageParticipationRate: number;
  totalBookingsGenerated: number;
  totalRevenueGenerated: number;
}

export interface PaginatedCampaignResponse {
  success: boolean;
  data: {
    campaigns: DriverCampaign[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
  error?: string;
}

class DriverCampaignService {
  private get apiBase(): string { return getApiBaseUrl(); }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.apiBase}${endpoint}`;
    const token = localStorage.getItem('token');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get all campaigns with filters and pagination
   */
  async getCampaigns(
    filters: CampaignFilters = {},
    page = 1,
    limit = 20
  ): Promise<PaginatedCampaignResponse> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }

      const response = await this.request<PaginatedCampaignResponse>(
        `/api/v1/admin/driver-campaigns?${params.toString()}`
      );

      return response;
    } catch (error: any) {
      console.error('Failed to get campaigns:', error);
      return {
        success: false,
        data: {
          campaigns: [],
          pagination: { page: 1, limit: 20, total: 0, pages: 0 }
        },
        error: error.message
      };
    }
  }

  /**
   * Get campaign statistics
   */
  async getStats(): Promise<{ success: boolean; data?: CampaignStats; error?: string }> {
    try {
      const response = await this.request<{ success: boolean; data: CampaignStats }>(
        '/api/v1/admin/driver-campaigns/stats'
      );
      return response;
    } catch (error: any) {
      console.error('Failed to get campaign stats:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get single campaign by ID
   */
  async getCampaign(id: string): Promise<{ success: boolean; data?: DriverCampaign; error?: string }> {
    try {
      const response = await this.request<{ success: boolean; data: DriverCampaign }>(
        `/api/v1/admin/driver-campaigns/${id}`
      );
      return response;
    } catch (error: any) {
      console.error('Failed to get campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create new campaign
   */
  async createCampaign(
    campaign: Omit<DriverCampaign, 'id' | 'createdAt' | 'updatedAt' | 'participation' | 'stats'>
  ): Promise<{ success: boolean; data?: DriverCampaign; error?: string }> {
    try {
      const response = await this.request<{ success: boolean; data: DriverCampaign }>(
        '/api/v1/admin/driver-campaigns',
        {
          method: 'POST',
          body: JSON.stringify(campaign),
        }
      );
      return response;
    } catch (error: any) {
      console.error('Failed to create campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update campaign
   */
  async updateCampaign(
    id: string,
    updates: Partial<DriverCampaign>
  ): Promise<{ success: boolean; data?: DriverCampaign; error?: string }> {
    try {
      const response = await this.request<{ success: boolean; data: DriverCampaign }>(
        `/api/v1/admin/driver-campaigns/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(updates),
        }
      );
      return response;
    } catch (error: any) {
      console.error('Failed to update campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete campaign
   */
  async deleteCampaign(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      await this.request(`/api/v1/admin/driver-campaigns/${id}`, {
        method: 'DELETE',
      });
      return { success: true };
    } catch (error: any) {
      console.error('Failed to delete campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Push campaign to mobile and notify drivers
   */
  async pushCampaign(id: string): Promise<{ success: boolean; data?: { driversNotified: number }; error?: string }> {
    try {
      const response = await this.request<{ success: boolean; data: { driversNotified: number } }>(
        `/api/v1/admin/driver-campaigns/${id}/push`,
        {
          method: 'POST',
        }
      );
      return response;
    } catch (error: any) {
      console.error('Failed to push campaign:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Activate campaign (change status to active)
   */
  async activateCampaign(id: string): Promise<{ success: boolean; data?: DriverCampaign; error?: string }> {
    return this.updateCampaign(id, { status: 'active' });
  }

  /**
   * End campaign (change status to ended)
   */
  async endCampaign(id: string): Promise<{ success: boolean; data?: DriverCampaign; error?: string }> {
    return this.updateCampaign(id, { status: 'ended' });
  }

  /**
   * Cancel campaign (change status to cancelled)
   */
  async cancelCampaign(id: string): Promise<{ success: boolean; data?: DriverCampaign; error?: string }> {
    return this.updateCampaign(id, { status: 'cancelled' });
  }

  /**
   * Get participation details for a campaign
   */
  async getParticipation(campaignId: string): Promise<{ success: boolean; data?: DriverParticipation[]; error?: string }> {
    try {
      const response = await this.request<{ success: boolean; data: DriverParticipation[] }>(
        `/api/v1/admin/driver-campaigns/${campaignId}/participation`
      );
      return response;
    } catch (error: any) {
      console.error('Failed to get participation:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new DriverCampaignService();
